import { useState } from 'react';
import { MeasurementDefinition } from '@easydone/shared';
import { useOrg } from '../contexts/OrgContext';
import {
  createMeasurementDefinition,
  updateMeasurementDefinition,
} from '../services/measurementDefinitionService';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface MeasurementDefinitionDialogProps {
  definition?: MeasurementDefinition;
  onClose: () => void;
}

export function MeasurementDefinitionDialog({
  definition,
  onClose,
}: MeasurementDefinitionDialogProps) {
  const { currentOrg } = useOrg();
  const isEditing = !!definition;

  const [formData, setFormData] = useState<Partial<MeasurementDefinition>>({
    name: definition?.name || '',
    description: definition?.description || '',
    category: definition?.category || 'General',
    dataType: definition?.dataType || 'number',
    unit: definition?.unit || '',
    precision: definition?.precision,
    minValue: definition?.minValue,
    maxValue: definition?.maxValue,
    context: definition?.context || 'both',
    trendable: definition?.trendable ?? true,
    aggregationType: definition?.aggregationType || 'avg',
    chartType: definition?.chartType || 'line',
    required: definition?.required ?? false,
    visible: definition?.visible ?? true,
    options: definition?.options || [],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOptionsChange = (value: string) => {
    const options = value.split('\n').filter((opt) => opt.trim() !== '');
    setFormData((prev) => ({
      ...prev,
      options,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;

    setError(null);
    setSaving(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.category || !formData.dataType) {
        throw new Error('Please fill in all required fields');
      }

      if (isEditing && definition) {
        await updateMeasurementDefinition(currentOrg.id, definition.id, formData);
      } else {
        await createMeasurementDefinition(currentOrg.id, formData as Omit<MeasurementDefinition, 'id'>);
      }

      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save measurement definition');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Measurement Definition' : 'Create Measurement Definition'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input w-full"
                  placeholder="e.g., Motor Current, Temperature, Vibration"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="input w-full"
                  rows={2}
                  placeholder="Brief description of what this measurement represents"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="input w-full"
                  placeholder="e.g., Electrical, Mechanical, Chemical"
                  required
                  list="category-suggestions"
                />
                <datalist id="category-suggestions">
                  <option value="Electrical" />
                  <option value="Mechanical" />
                  <option value="Chemical" />
                  <option value="Thermal" />
                  <option value="Pressure" />
                  <option value="Calibration" />
                  <option value="Performance" />
                  <option value="Environmental" />
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Context <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.context}
                  onChange={(e) => handleChange('context', e.target.value)}
                  className="input w-full"
                  required
                >
                  <option value="both">Both FLOC & Asset</option>
                  <option value="floc">FLOC Only</option>
                  <option value="asset">Asset Only</option>
                </select>
              </div>
            </div>

            {/* Data Type Configuration */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Data Type Configuration</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Type <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={formData.dataType}
                    onChange={(e) => handleChange('dataType', e.target.value)}
                    className="input w-full"
                    required
                  >
                    <option value="number">Number</option>
                    <option value="text">Text</option>
                    <option value="boolean">Boolean (Yes/No)</option>
                    <option value="date">Date</option>
                    <option value="select">Select (Dropdown)</option>
                  </select>
                </div>

                {formData.dataType === 'number' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={formData.unit}
                        onChange={(e) => handleChange('unit', e.target.value)}
                        className="input w-full"
                        placeholder="e.g., amps, °C, psi, rpm"
                        list="unit-suggestions"
                      />
                      <datalist id="unit-suggestions">
                        <option value="amps" />
                        <option value="volts" />
                        <option value="°C" />
                        <option value="°F" />
                        <option value="psi" />
                        <option value="bar" />
                        <option value="rpm" />
                        <option value="Hz" />
                        <option value="pH" />
                        <option value="%" />
                      </datalist>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precision (Decimal Places)
                      </label>
                      <input
                        type="number"
                        value={formData.precision ?? ''}
                        onChange={(e) =>
                          handleChange('precision', e.target.value === '' ? undefined : parseInt(e.target.value))
                        }
                        className="input w-full"
                        min="0"
                        max="10"
                        placeholder="2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Value
                      </label>
                      <input
                        type="number"
                        value={formData.minValue ?? ''}
                        onChange={(e) =>
                          handleChange('minValue', e.target.value === '' ? undefined : parseFloat(e.target.value))
                        }
                        className="input w-full"
                        step="any"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Value
                      </label>
                      <input
                        type="number"
                        value={formData.maxValue ?? ''}
                        onChange={(e) =>
                          handleChange('maxValue', e.target.value === '' ? undefined : parseFloat(e.target.value))
                        }
                        className="input w-full"
                        step="any"
                      />
                    </div>
                  </>
                )}

                {formData.dataType === 'select' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Options (one per line)
                    </label>
                    <textarea
                      value={formData.options?.join('\n') || ''}
                      onChange={(e) => handleOptionsChange(e.target.value)}
                      className="input w-full"
                      rows={5}
                      placeholder="Good&#10;Fair&#10;Poor&#10;Failed"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Info about thresholds */}
            {formData.dataType === 'number' && (
              <div className="border-t pt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    About Thresholds
                  </h4>
                  <p className="text-sm text-blue-800">
                    Warning and critical thresholds are installation-specific and depend on design ratings.
                    They will be configured when adding this measurement to templates or at the FLOC/Asset level.
                  </p>
                </div>
              </div>
            )}

            {/* Trending Configuration */}
            {formData.dataType === 'number' && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Trending Configuration</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.trendable}
                        onChange={(e) => handleChange('trendable', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable Trending</span>
                    </label>
                  </div>

                  {formData.trendable && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Aggregation Type
                        </label>
                        <select
                          value={formData.aggregationType}
                          onChange={(e) => handleChange('aggregationType', e.target.value)}
                          className="input w-full"
                        >
                          <option value="avg">Average</option>
                          <option value="sum">Sum</option>
                          <option value="min">Minimum</option>
                          <option value="max">Maximum</option>
                          <option value="last">Last Value</option>
                          <option value="count">Count</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Chart Type
                        </label>
                        <select
                          value={formData.chartType}
                          onChange={(e) => handleChange('chartType', e.target.value)}
                          className="input w-full"
                        >
                          <option value="line">Line Chart</option>
                          <option value="bar">Bar Chart</option>
                          <option value="gauge">Gauge</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Additional Options */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Options</h3>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.required}
                    onChange={(e) => handleChange('required', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Required</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.visible}
                    onChange={(e) => handleChange('visible', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Visible in Forms</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditing ? 'Update Definition' : 'Create Definition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
